import type { InputProps } from './type';
import { css, cx } from '@mildang/styled-system/css';
import { input } from '@mildang/styled-system/recipes';
import { splitCssProps } from '@mildang/styled-system/jsx';
import { forwardRef } from 'react';
import { migrateSize } from '../../lib/migrateSize';

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    size = 'md',
    fullWidth = false,
    error = false,
    warning = false,
    success = false,
    disabled = false,
    readOnly = false,
    startAdornment,
    endAdornment,
    className,
    inputContainerClassName,
    inputElementClassName,
    _containerId,
    ghost = false,
    sx,
    ...other
  } = props;

  // restProps 를 받아서 `<input>` 에 뿌린다. 원본 `other` 를 뿌리면 css prop 이
  // root div 의 panda 클래스로 한 번, native `<input>` 의 raw 속성으로 또 한 번 나간다
  // (`<Input width="30px" />` → `<input width="30px">`, input 스펙에 없는 속성).
  const [cssProps, restProps] = splitCssProps(other);
  const { css: _cssStyleProps, ...styleProps } = cssProps;

  const _size = migrateSize(size as string) as typeof size;
  const classes = input({
    size: _size,
    error,
    warning,
    success,
    fullWidth,
    ghost,
  });

  return (
    <div className={cx(classes.root, css(styleProps, sx), className)}>
      <div
        className={cx(classes.inputContainer, inputContainerClassName)}
        data-disabled={disabled ? 'true' : undefined}
        data-readonly={readOnly ? 'true' : undefined}
        id={_containerId}
      >
        {startAdornment && <div className={classes.startAdornment}>{startAdornment}</div>}
        <input
          ref={ref}
          {...restProps}
          disabled={disabled}
          readOnly={readOnly}
          className={cx(classes.input, inputElementClassName)}
        />
        {endAdornment && <div className={classes.endAdornment}>{endAdornment}</div>}
      </div>
    </div>
  );
});

Input.displayName = 'Input';
